import type { MaybeNode } from '@proton/drive';
import { isIWAD, isVideo } from '@proton/shared/lib/helpers/mimetype';
import { isPreviewAvailable } from '@proton/shared/lib/helpers/preview';

import { getNodeDisplaySize } from '../../utils/sdk/getNodeDisplaySize';
import { streamToBuffer } from '../../utils/stream';
import { loadCreateReadableStreamWrapper } from '../../utils/webStreamsPolyfill';
import { getNodeMimeType } from './nodeUtils';

export enum ContentPreviewMethod {
    Buffer = 'buffer',
    Streaming = 'streaming',
    Thumbnail = 'thumbnail',
}

interface GetFileDownloader {
    getFileDownloader: (nodeUid: string, abortSignal: AbortSignal) => Promise<FileDownloader>;
    getFileRevisionDownloader?: (nodeRevisionUid: string, abortSignal: AbortSignal) => Promise<FileDownloader>;
}

interface FileDownloader {
    downloadToStream: (
        writable: WritableStream<Uint8Array<ArrayBuffer>>,
        onProgress: (downloadedBytes: number) => void
    ) => DownloadController;
}

interface DownloadController {
    pause: () => void;
    resume: () => void;
    completion: () => Promise<void>;
    isDownloadCompleteWithSignatureIssues: () => boolean;
}

export function getContentPreviewMethod(node: MaybeNode): ContentPreviewMethod {
    const mimeType = getNodeMimeType(node);

    if (!mimeType) {
        return ContentPreviewMethod.Thumbnail;
    }

    if (isVideo(mimeType)) {
        return ContentPreviewMethod.Streaming;
    }

    if (isPreviewAvailable(mimeType, getNodeDisplaySize(node)) || isIWAD(mimeType)) {
        return ContentPreviewMethod.Buffer;
    }

    return ContentPreviewMethod.Thumbnail;
}

export async function downloadContent(
    drive: GetFileDownloader,
    nodeUid: string,
    abortSignal: AbortSignal,
    revisionUid?: string
) {
    const transformStream = new TransformStream<Uint8Array<ArrayBuffer>>();
    const stream = await loadCreateReadableStreamWrapper(transformStream.readable);
    const writer = transformStream.writable.getWriter();

    const wrappedWritable = new WritableStream<Uint8Array<ArrayBuffer>>({
        write: (chunk) => writer.write(chunk),
        close: () => writer.close(),
        abort: (reason) => writer.abort(reason),
    });

    const downloader =
        revisionUid && drive.getFileRevisionDownloader
            ? await drive.getFileRevisionDownloader(revisionUid, abortSignal)
            : await drive.getFileDownloader(nodeUid, abortSignal);
    const controller = downloader.downloadToStream(wrappedWritable, () => {});

    const bufferPromise = streamToBuffer(stream);

    try {
        await controller.completion();
        await writer.close();
    } catch (error) {
        if (controller.isDownloadCompleteWithSignatureIssues()) {
            await writer.close();
            return { contents: await bufferPromise, hasSignatureIssues: true };
        }

        await writer.abort(error).catch(() => {});
        throw error;
    } finally {
        try {
            writer.releaseLock();
        } catch {
            // Some browser handle releaseLock in a way where it can throw TypeError.
            // This is fine but as it can cause crash in the logic we hanld it here
        }
    }

    return { contents: await bufferPromise, hasSignatureIssues: false };
}
